import { useQuery } from '@apollo/client';
import { gql } from '@apollo/client';

const GET_BRAND_RELATIONS = gql`
  query GetBrandRelations(
    $brand_id: Int
    $brand_name: String
  ) {
    getBrandRelations(
      brand_id: $brand_id
      brand_name: $brand_name
    ) {
      source_id
      source_name
      source_resizedSmallLogo
      target_id
      target_name
      target_resizedSmallLogo
      level
      similar_rank
      score
    }
  }
`;

/**
 * Fetch brand relations graph data.
 * @param {Object} options - Query options
 * @param {string} [options.brandName] - Brand name to center the graph on
 * @param {number} [options.brandId] - Brand ID to center the graph on
 * @returns {Object} Query result with data, loading, error, and refetch
 */
export default function useBrandRelations({
  brandName,
  brandId,
} = {}) {
  const { data, loading, error, refetch } = useQuery(GET_BRAND_RELATIONS, {
    variables: {
      brand_name: brandName || undefined,
      brand_id: brandId || undefined,
    },
    skip: !brandName && !brandId,
    fetchPolicy: 'cache-and-network',
    ssr: false,
  });

  return {
    relations: data?.getBrandRelations || [],
    loading,
    error,
    refetch,
  };
}

/**
 * Transform API response to legacy format for compatibility.
 * Converts BrandRelationEdge[] to [source_name, target_name, level][] format.
 * @param {Array} edges - Array of BrandRelationEdge from API
 * @returns {Array} Legacy format relations
 */
export function transformToLegacyFormat(edges) {
  if (!edges || !edges.length) return [];
  return edges.map((edge) => [edge.source_name, edge.target_name, edge.level]);
}

/**
 * Build adjacency maps from API edges.
 * @param {Array} edges - Array of BrandRelationEdge from API
 * @returns {Object} { dirSim, adj } adjacency maps
 */
export function buildAdjacencyMapsFromEdges(edges) {
  const dirSim = {};
  const adj = {};

  edges.forEach((edge) => {
    const source = edge.source_name;
    const target = edge.target_name;

    if (!dirSim[source]) dirSim[source] = new Set();
    dirSim[source].add(target);

    if (!adj[source]) adj[source] = new Set();
    if (!adj[target]) adj[target] = new Set();
    adj[source].add(target);
    adj[target].add(source);
  });

  return { dirSim, adj };
}

/**
 * Build brand logo lookup from API edges.
 * @param {Array} edges - Array of BrandRelationEdge from API
 * @returns {Object} { [brandName]: logoUrl }
 */
export function buildBrandLogoMapFromEdges(edges) {
  const logos = {};

  if (!edges || !edges.length) return logos;

  edges.forEach((edge) => {
    if (edge.source_name && edge.source_resizedSmallLogo) {
      logos[edge.source_name] = edge.source_resizedSmallLogo;
    }

    if (edge.target_name && edge.target_resizedSmallLogo) {
      logos[edge.target_name] = edge.target_resizedSmallLogo;
    }
  });

  return logos;
}

/**
 * Get all unique brand names from API edges.
 * @param {Array} edges - Array of BrandRelationEdge from API
 * @returns {Array} Sorted array of unique brand names
 */
export function getAllBrandsFromEdges(edges) {
  if (!edges || !edges.length) return [];
  const brands = new Set();
  edges.forEach((edge) => {
    brands.add(edge.source_name);
    brands.add(edge.target_name);
  });
  return [...brands].sort();
}
